import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useMemo, useCallback } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import CloseIcon from "@mui/icons-material/Close";
import { useLayout } from "../contexts/LayoutContext";
import ColumnHeaderMenu from "./ColumnHeaderMenu";
import ColumnVisibilityPanel from "./ColumnVisibilityPanel";
import InfoTooltip from "./InfoTooltip";
import { flattenColumns, normalizeColumns, toSet, getColumnAlign as getColumnAlignUtil, hasGroupedColumns, getMaxColumnDepth, } from "./tableUtils";
import { COLUMN_WIDTHS, EXPAND_TRIGGERS, SUMMARY_ROW_POSITIONS, SORT_DIRECTIONS, FILTER_TYPES, DEFAULT_MESSAGES, Z_INDEX, DRAG_DROP, } from "./tableConstants";
import CommonLoader from "./CommonLoader";
function CommonTable({ columns = [], rows = [], renderCell, renderExpandedContent, expandTrigger = EXPAND_TRIGGERS.ICON, onExpandToggle, sortConfig = null, onSortChange, onRowClick, isLoading = false, isFirstLoad = false, isError = false, emptyMessage = DEFAULT_MESSAGES.NO_DATA, renderEmpty, renderError, renderLoading, type = "list", enableSelection = false, isRowSelectable, selectedRows = new Set(), onSelectionChange, beforeSelectionColumn = null, hiddenColumns = new Set(), onColumnVisibilityChange, columnVisibilityAnchorEl = null, onColumnVisibilityClose, enableColumnReorder = false, columnOrder = null, onColumnOrderChange, enableColumnPinning = false, pinnedColumns = { left: [], right: [] }, onColumnPinningChange, enableColumnActions = false, enableColumnFilters = false, showFilterRow = false, onFilterRowToggle, activeFilterColumnId, onActiveFilterColumnIdChange, columnFilters = {}, onColumnFilterChange, enableSummaryRow = false, renderSummaryRow, summaryRowPosition = SUMMARY_ROW_POSITIONS.BOTTOM, columnVisibilityConfig = null, columnFilterConfig = null, controlledExpandedRows = null, tableHeadClassName = "reusable-table-header-label", expandCellClassName = "reusable-table-cell-expanded-content", stickyHeader = false, maxHeight = "500px", rowSx, rowProps, isRowExpandable, onExpandAllToggle, }) {
    const { isDense } = useLayout();
    // Internal state
    const [columnVisibilityOpen, setColumnVisibilityOpen] = useState(false);
    const [columnVisibilityAnchor, setColumnVisibilityAnchor] = useState(null);
    const [draggedColumnId, setDraggedColumnId] = useState(null);
    const [dragOverColumnId, setDragOverColumnId] = useState(null);
    const [columnOriginalPositions, setColumnOriginalPositions] = useState({});
    const [internalActiveFilterColumnId, setInternalActiveFilterColumnId] = useState(null);
    const [internalExpandedRows, setInternalExpandedRows] = useState(new Set());
    const [filterEnabledColumns, setFilterEnabledColumns] = useState(new Set());
    // Computed values
    const effectiveActiveFilterColumnId = activeFilterColumnId !== undefined
        ? activeFilterColumnId
        : internalActiveFilterColumnId;
    const effectiveAnchor = columnVisibilityAnchorEl || columnVisibilityAnchor;
    const isControlledExpand = typeof onExpandToggle === "function";
    const expandedRowsSet = isControlledExpand
        ? new Set(controlledExpandedRows || [])
        : internalExpandedRows;
    const hiddenColumnsSet = toSet(hiddenColumns);
    const selectedRowsSet = toSet(selectedRows);
    // Display flags
    const showExpandColumn = expandTrigger !== EXPAND_TRIGGERS.NONE;
    const showSelectionColumn = enableSelection;
    const showBeforeSelectionColumn = Boolean(beforeSelectionColumn);
    // Normalize columns with memoization
    const normalizedColumns = useMemo(() => {
        return normalizeColumns(columns, hiddenColumnsSet, columnOrder, pinnedColumns);
    }, [columns, hiddenColumnsSet, columnOrder, pinnedColumns]);
    // Get visible columns (flattened for rendering)
    const visibleColumns = useMemo(() => {
        const allCols = [
            ...normalizedColumns.leftPinned,
            ...normalizedColumns.unpinned,
            ...normalizedColumns.rightPinned,
        ];
        return flattenColumns(allCols);
    }, [normalizedColumns]);
    // Selection state
    const isAllSelected = useMemo(() => {
        if (!enableSelection || rows.length === 0)
            return false;
        const selectableRows = isRowSelectable ? rows.filter(isRowSelectable) : rows;
        if (selectableRows.length === 0)
            return false;
        return selectableRows.every((row) => {
            const rowId = row.id ?? rows.indexOf(row);
            return selectedRowsSet.has(rowId);
        });
    }, [enableSelection, rows, selectedRowsSet, isRowSelectable]);
    const isIndeterminate = useMemo(() => {
        if (!enableSelection)
            return false;
        const selectableRows = isRowSelectable ? rows.filter(isRowSelectable) : rows;
        const hasSelected = selectableRows.some((row) => {
            const rowId = row.id ?? rows.indexOf(row);
            return selectedRowsSet.has(rowId);
        });
        return hasSelected && !isAllSelected;
    }, [enableSelection, rows, selectedRowsSet, isAllSelected, isRowSelectable]);
    // Column width and alignment helpers
    const getColumnWidth = useCallback((column) => {
        if (column?.width)
            return column.width;
        if (column?.id === "expand")
            return COLUMN_WIDTHS.EXPAND;
        if (column?.id === "selection")
            return COLUMN_WIDTHS.SELECTION;
        return COLUMN_WIDTHS.DEFAULT;
    }, []);
    const getColumnAlign = useCallback((column) => {
        return getColumnAlignUtil(column, "left");
    }, []);
    // Calculate pinning offsets
    const leftPinnedOffsets = useMemo(() => {
        const offsets = {};
        let currentOffset = 0;
        // Selection column if it's pinned left
        if (showSelectionColumn && pinnedColumns.left.includes("selection")) {
            offsets["selection"] = currentOffset;
            currentOffset += parseInt(COLUMN_WIDTHS.SELECTION);
        }
        // Expand column if it's pinned left
        if (showExpandColumn && pinnedColumns.left.includes("expand")) {
            offsets["expand"] = currentOffset;
            currentOffset += parseInt(COLUMN_WIDTHS.EXPAND);
        }
        normalizedColumns.leftPinned.forEach((col) => {
            offsets[col.id] = currentOffset;
            const width = getColumnWidth(col);
            currentOffset += typeof width === "number" ? width : parseInt(String(width)) || 100;
        });
        return offsets;
    }, [showSelectionColumn, showExpandColumn, pinnedColumns.left, normalizedColumns.leftPinned, getColumnWidth]);
    const rightPinnedOffsets = useMemo(() => {
        const offsets = {};
        let currentOffset = 0;
        [...normalizedColumns.rightPinned].reverse().forEach((col) => {
            offsets[col.id] = currentOffset;
            const width = getColumnWidth(col);
            currentOffset += typeof width === "number" ? width : parseInt(String(width)) || 100;
        });
        return offsets;
    }, [normalizedColumns.rightPinned, getColumnWidth]);
    // Loading state logic
    const showFullLoading = isLoading && isFirstLoad;
    const showBodyLoading = isLoading && !isFirstLoad;
    // Event handlers
    const handleExpandClick = useCallback((rowId, e) => {
        if (e) {
            e.stopPropagation();
        }
        if (isControlledExpand && onExpandToggle) {
            onExpandToggle(rowId);
        }
        else {
            setInternalExpandedRows((prev) => {
                const next = new Set(prev);
                if (next.has(rowId)) {
                    next.delete(rowId);
                }
                else {
                    next.add(rowId);
                }
                return next;
            });
        }
    }, [isControlledExpand, onExpandToggle]);
    const handleRowClick = useCallback((row, rowId) => {
        if (expandTrigger === EXPAND_TRIGGERS.ROW && onExpandToggle) {
            onExpandToggle(rowId);
        }
        if (onRowClick) {
            onRowClick(row);
        }
    }, [expandTrigger, onExpandToggle, onRowClick]);
    const handleSort = useCallback((columnId, direction = null) => {
        if (!onSortChange)
            return;
        if (direction) {
            onSortChange(columnId, direction);
        }
        else {
            const isCurrentSort = sortConfig?.columnId === columnId;
            const newDirection = isCurrentSort
                ? sortConfig.direction === SORT_DIRECTIONS.ASC
                    ? SORT_DIRECTIONS.DESC
                    : SORT_DIRECTIONS.ASC
                : SORT_DIRECTIONS.DESC;
            onSortChange(columnId, newDirection);
        }
    }, [onSortChange, sortConfig]);
    const handleSelectAll = useCallback((event) => {
        if (!enableSelection || !onSelectionChange)
            return;
        const newSelected = new Set(selectedRowsSet);
        const selectableRows = isRowSelectable ? rows.filter(isRowSelectable) : rows;
        if (event.target.checked) {
            selectableRows.forEach((row) => {
                const rowId = row.id ?? rows.indexOf(row);
                newSelected.add(rowId);
            });
        }
        else {
            selectableRows.forEach((row) => {
                const rowId = row.id ?? rows.indexOf(row);
                newSelected.delete(rowId);
            });
        }
        onSelectionChange(newSelected);
    }, [enableSelection, onSelectionChange, rows, selectedRowsSet, isRowSelectable]);
    const handleRowSelect = useCallback((rowId, event) => {
        if (!enableSelection || !onSelectionChange)
            return;
        event.stopPropagation();
        const newSelected = new Set(selectedRowsSet);
        if (newSelected.has(rowId)) {
            newSelected.delete(rowId);
        }
        else {
            newSelected.add(rowId);
        }
        onSelectionChange(newSelected);
    }, [enableSelection, onSelectionChange, selectedRowsSet]);
    const handleColumnVisibilityChange = useCallback((newHiddenColumns) => {
        if (onColumnVisibilityChange) {
            onColumnVisibilityChange(newHiddenColumns);
        }
    }, [onColumnVisibilityChange]);
    const handleColumnOrderChange = useCallback((newOrder) => {
        if (onColumnOrderChange) {
            onColumnOrderChange(newOrder);
        }
    }, [onColumnOrderChange]);
    const handleColumnPinningChange = useCallback((columnId, position) => {
        if (!onColumnPinningChange)
            return;
        const newPinned = { ...pinnedColumns };
        if (position === null) {
            // Unpin and restore original position
            newPinned.left = newPinned.left.filter((id) => id !== columnId);
            newPinned.right = newPinned.right.filter((id) => id !== columnId);
            if (onColumnOrderChange && columnOriginalPositions[columnId] !== undefined) {
                const currentOrder = columnOrder ||
                    normalizedColumns.all.map((col) => col.id).filter(Boolean);
                const newOrder = [...currentOrder];
                const currentIndex = newOrder.indexOf(columnId);
                if (currentIndex !== -1) {
                    newOrder.splice(currentIndex, 1);
                }
                const originalIndex = columnOriginalPositions[columnId];
                if (originalIndex !== undefined) {
                    newOrder.splice(originalIndex, 0, columnId);
                    onColumnOrderChange(newOrder);
                }
                setColumnOriginalPositions((prev) => {
                    const updated = { ...prev };
                    delete updated[columnId];
                    return updated;
                });
            }
        }
        else {
            // Store original position before pinning
            const wasPinned = newPinned.left.includes(columnId);
            if (!wasPinned && onColumnOrderChange) {
                const currentOrder = columnOrder ||
                    normalizedColumns.all.map((col) => col.id).filter(Boolean);
                const currentIndex = currentOrder.indexOf(columnId);
                if (currentIndex !== -1 && !columnOriginalPositions[columnId]) {
                    setColumnOriginalPositions((prev) => ({
                        ...prev,
                        [columnId]: currentIndex,
                    }));
                }
            }
            // Remove from all positions first
            newPinned.left = newPinned.left.filter((id) => id !== columnId);
            newPinned.right = newPinned.right.filter((id) => id !== columnId);
            // Add to new position
            if (position === "left") {
                newPinned.left.push(columnId);
            }
            else if (position === "right") {
                newPinned.right.push(columnId);
            }
        }
        onColumnPinningChange(newPinned);
    }, [
        onColumnPinningChange,
        pinnedColumns,
        columnOrder,
        normalizedColumns,
        columnOriginalPositions,
        onColumnOrderChange,
    ]);
    const handleClearSort = useCallback(() => {
        if (onSortChange) {
            onSortChange(null, null);
        }
    }, [onSortChange]);
    // Drag and drop handlers
    const handleHeaderDragStart = useCallback((e, columnId) => {
        if (!enableColumnReorder)
            return;
        setDraggedColumnId(columnId);
        e.dataTransfer.effectAllowed = DRAG_DROP.EFFECT_ALLOWED;
        e.dataTransfer.setData(DRAG_DROP.DATA_TYPE, columnId);
    }, [enableColumnReorder]);
    const handleHeaderDragOver = useCallback((e, columnId) => {
        if (!enableColumnReorder || !draggedColumnId || draggedColumnId === columnId)
            return;
        e.preventDefault();
        e.dataTransfer.dropEffect = DRAG_DROP.EFFECT_ALLOWED;
        setDragOverColumnId(columnId);
    }, [enableColumnReorder, draggedColumnId]);
    const handleHeaderDragLeave = useCallback(() => {
        setDragOverColumnId(null);
    }, []);
    const handleHeaderDrop = useCallback((e, targetColumnId) => {
        if (!enableColumnReorder || !draggedColumnId || !onColumnOrderChange)
            return;
        e.preventDefault();
        const visibleColumns = normalizedColumns.all.filter((col) => !hiddenColumnsSet.has(col.id));
        const currentOrder = columnOrder || visibleColumns.map((col) => col.id).filter(Boolean);
        const fromIndex = currentOrder.indexOf(draggedColumnId);
        const toIndex = currentOrder.indexOf(targetColumnId);
        if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
            setDraggedColumnId(null);
            setDragOverColumnId(null);
            return;
        }
        const newOrder = [...currentOrder];
        const [removed] = newOrder.splice(fromIndex, 1);
        newOrder.splice(toIndex, 0, removed);
        onColumnOrderChange(newOrder);
        setDraggedColumnId(null);
        setDragOverColumnId(null);
    }, [
        enableColumnReorder,
        draggedColumnId,
        onColumnOrderChange,
        columnOrder,
        normalizedColumns,
        hiddenColumnsSet,
    ]);
    const handleHeaderDragEnd = useCallback(() => {
        setDraggedColumnId(null);
        setDragOverColumnId(null);
    }, []);
    const handleCloseColumnVisibility = useCallback(() => {
        setColumnVisibilityOpen(false);
        setColumnVisibilityAnchor(null);
        if (onColumnVisibilityClose) {
            onColumnVisibilityClose();
        }
    }, [onColumnVisibilityClose]);
    // Render functions
    const renderFilterCell = useCallback((column) => {
        if (!columnFilterConfig?.enable)
            return null;
        const colConfig = columnFilterConfig.columns?.[column.id];
        if (!colConfig)
            return null;
        const hasValue = !!columnFilters[column.id];
        // Global toolbar filter mode (icon se): sabhi columns ke filters dikhne chahiye
        const isGlobalFilterMode = !!showFilterRow && !effectiveActiveFilterColumnId;
        // Single / per-column mode:
        // Column ka filter input sirf tab dikhao jab:
        // - us column ka filter explicitly enable kiya gaya ho (Filter by click se), YA
        // - us column me already koi value ho
        if (!isGlobalFilterMode && !filterEnabledColumns.has(column.id) && !hasValue) {
            return null;
        }
        const value = columnFilters[column.id] || "";
        // Dropdown filter
        if (colConfig.type === FILTER_TYPES.DROPDOWN) {
            return (_jsxs(Select, { className: "table-header-select", fullWidth: true, size: "small", value: value, onChange: (e) => onColumnFilterChange?.(column.id, e.target.value), displayEmpty: true, children: [_jsx(MenuItem, { value: "", children: _jsx("em", { children: "All" }) }), (colConfig.options || []).map((opt) => (_jsx(MenuItem, { value: opt.value, children: opt.label }, opt.value)))] }));
        }
        // Search input (default)
        return (_jsx(TextField, { fullWidth: true, size: "small", placeholder: `Filter ${column.label}`, value: value, onChange: (e) => onColumnFilterChange?.(column.id, e.target.value), InputProps: {
                endAdornment: value && (_jsx(IconButton, { size: "small", onClick: () => onColumnFilterChange?.(column.id, ""), children: _jsx(CloseIcon, { fontSize: "small" }) })),
            } }));
    }, [
        columnFilterConfig,
        effectiveActiveFilterColumnId,
        columnFilters,
        filterEnabledColumns,
        onColumnFilterChange,
        showFilterRow,
    ]);
    const renderColumnHeader = useCallback((column) => {
        const isSortable = column.sortable && onSortChange;
        const isSorted = sortConfig?.columnId === column.id;
        const sortDirection = isSorted ? sortConfig.direction : SORT_DIRECTIONS.ASC;
        return (_jsxs(Box, { sx: {
                display: "flex",
                alignItems: "center",
                width: "100%",
                justifyContent: getColumnAlign(column) === "right"
                    ? "flex-end !important"
                    : getColumnAlign(column) === "center"
                        ? "center !important"
                        : "flex-start !important",
                gap: 0.5,
            }, children: [enableColumnReorder && column.draggable !== false && (_jsx(IconButton, { size: "small", draggable: true, onDragStart: (e) => {
                        e.stopPropagation();
                        handleHeaderDragStart(e, column.id);
                    }, onDragEnd: (e) => {
                        e.stopPropagation();
                        handleHeaderDragEnd();
                    }, onClick: (e) => e.stopPropagation(), sx: {
                        padding: "2px",
                        opacity: 0.6,
                        cursor: "grab",
                        "&:active": { cursor: "grabbing" },
                        "&:hover": { opacity: 1 },
                    }, children: _jsx("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M10.667 4a.667.667 0 1 0 0-1.333.667.667 0 0 0 0 1.333m0 4.667a.667.667 0 1 0 0-1.334.667.667 0 0 0 0 1.334m0 4.666a.667.667 0 1 0 0-1.333.667.667 0 0 0 0 1.333M5.333 4a.667.667 0 1 0 0-1.333.667.667 0 0 0 0 1.333m0 4.667a.667.667 0 1 0 0-1.334.667.667 0 0 0 0 1.334m0 4.666a.667.667 0 1 0 0-1.333.667.667 0 0 0 0 1.333", stroke: "#a1a1aa", strokeWidth: "1.333", strokeLinecap: "round", strokeLinejoin: "round" }) }) })), _jsx(Box, { className: tableHeadClassName, sx: { fontSize: '12px !important' }, children: column.label }), column.tooltip && (_jsx(InfoTooltip, { title: column.tooltip, size: 14, sx: { ml: 0.5 } })), isSortable && (_jsx(Box, { className: "reusable-table-sort-icon", sx: { ml: 0.5, cursor: "pointer" }, onClick: (e) => {
                        e.stopPropagation();
                        handleSort(column.id);
                    }, children: sortDirection === SORT_DIRECTIONS.ASC ? (_jsx("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M2.667 11.333h8M2.667 8h6m-6-3.333h4m5.333 4V3.333m0 0 2 2m-2-2-2 2", stroke: "#181d27", strokeLinecap: "round", strokeLinejoin: "round" }) })) : (_jsx("svg", { width: "16", height: "16", viewBox: "0 0 16 16", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M2.667 11.333h4M2.667 8h6M12 7.333v5.334m0 0 2-2m-2 2-2-2m-7.333-6h8", stroke: "#181d27", strokeLinecap: "round", strokeLinejoin: "round" }) })) })), enableColumnActions && column.showColumnActions !== false && (_jsx(ColumnHeaderMenu, { column: column, sortConfig: sortConfig, onSort: handleSort, onClearSort: handleClearSort, onPin: handleColumnPinningChange, onFilter: (col, clear) => {
                        if (clear) {
                            const hasValue = !!columnFilters[col.id];
                            const otherActiveFiltersByValue = Object.keys(columnFilters).filter((id) => id !== col.id && !!columnFilters[id]);
                            const hasOtherEnabledColumns = filterEnabledColumns.size > 1;
                            // 1️⃣ value thi tabhi API trigger
                            if (hasValue) {
                                onColumnFilterChange(col.id, "");
                            }
                            // 2️⃣ sirf isi column ka mode close
                            if (activeFilterColumnId === col.id) {
                                onActiveFilterColumnIdChange?.(null);
                            }
                            // 3️⃣ is column ko enabled list se hatao
                            setFilterEnabledColumns((prev) => {
                                const next = new Set(prev);
                                next.delete(col.id);
                                return next;
                            });
                            // 4️⃣ Filter row sirf tab band karo jab:
                            //    - koi aur column enabled nahi bacha
                            //    - aur kisi aur column me value bhi nahi hai
                            if (!hasOtherEnabledColumns && otherActiveFiltersByValue.length === 0) {
                                onFilterRowToggle?.({ mode: "single", columnId: col.id, open: false });
                            }
                            return;
                        }
                        // Yahan par user ne "Filter by ..." click kiya
                        // -> is column ko enabled set me add karo
                        setFilterEnabledColumns((prev) => {
                            const next = new Set(prev);
                            next.add(col.id);
                            return next;
                        });
                        onActiveFilterColumnIdChange?.(col.id);
                        onFilterRowToggle?.({ mode: "single", columnId: col.id, open: true });
                        if (onActiveFilterColumnIdChange) {
                            onActiveFilterColumnIdChange(col.id);
                        }
                        else {
                            setInternalActiveFilterColumnId(col.id);
                        }
                        if (!showFilterRow && onFilterRowToggle) {
                            onFilterRowToggle({ mode: "single", columnId: col.id, open: true });
                        }
                    }, columnFilters: columnFilters, pinnedColumns: pinnedColumns, enableColumnPinning: enableColumnPinning, enableColumnFilters: enableColumnFilters, activeFilterColumnId: effectiveActiveFilterColumnId, showFilterRow: showFilterRow, enabledFilterColumnIds: filterEnabledColumns }))] }));
    }, [
        onSortChange,
        sortConfig,
        enableColumnReorder,
        handleHeaderDragStart,
        handleHeaderDragEnd,
        handleSort,
        getColumnAlign,
        enableColumnActions,
        handleClearSort,
        handleColumnPinningChange,
        onColumnFilterChange,
        onActiveFilterColumnIdChange,
        showFilterRow,
        onFilterRowToggle,
        columnFilters,
        pinnedColumns,
        enableColumnPinning,
        enableColumnFilters,
    ]);
    const renderGroupedHeader = useCallback(() => {
        if (!hasGroupedColumns(columns))
            return null;
        const headerRows = [];
        const maxDepth = getMaxColumnDepth(columns);
        const injectedCount = (showExpandColumn ? 1 : 0) + (showBeforeSelectionColumn ? 1 : 0) + (showSelectionColumn ? 1 : 0);
        // First row: Parent columns
        headerRows.push(_jsx(TableRow, { className: tableHeadClassName, children: normalizedColumns.all.map((column, index) => {
                const isFirst = index === 0;
                const effectiveColSpan = (column.children?.length || 1) + (isFirst ? injectedCount : 0);
                if (column.children && column.children.length > 0) {
                    return (_jsx(TableCell, { "data-column-id": column.id, colSpan: effectiveColSpan, className: "reusable-table-header-cell reusable-table-header-cell-group", sx: { fontSize: '12px !important', fontWeight: '700 !important', textTransform: 'capitalize' }, style: {
                            width: COLUMN_WIDTHS.DEFAULT,
                            textAlign: isFirst ? 'left' : getColumnAlign(column),
                            ...(stickyHeader && {
                                position: "sticky",
                                top: 0,
                                zIndex: 11,
                                backgroundColor: '#f4f4f5',
                            })
                        }, align: isFirst ? 'left' : getColumnAlign(column), children: column.renderHeader
                            ? column.renderHeader(column)
                            : column.label }, column.id));
                }
                // Non-grouped columns
                return (_jsx(TableCell, { "data-column-id": column.id, rowSpan: maxDepth, colSpan: isFirst ? injectedCount + 1 : 1, className: `reusable-table-header-cell ${column.sortable && onSortChange
                        ? "reusable-table-header-cell-sortable"
                        : ""}`, style: {
                        width: getColumnWidth(column),
                        textAlign: getColumnAlign(column),
                        verticalAlign: "middle",
                        ...(stickyHeader && {
                            position: "sticky",
                            top: 0,
                            zIndex: 11,
                            backgroundColor: '#f4f4f5',
                        })
                    }, align: getColumnAlign(column), children: renderColumnHeader(column) }, column.id));
            }) }, "header-parent"));
        // Second row: Child columns
        headerRows.push(_jsxs(TableRow, { className: "reusable-table-header", children: [showExpandColumn && (_jsx(TableCell, { className: "reusable-table-header-cell reusable-table-header-cell-expand", style: {
                        width: COLUMN_WIDTHS.EXPAND,
                        ...(stickyHeader && {
                            position: "sticky",
                            top: 46,
                            zIndex: 10,
                            backgroundColor: '#f4f4f5',
                        })
                    } })), showBeforeSelectionColumn && (_jsx(TableCell, { className: `reusable-table-header-cell ${beforeSelectionColumn?.className || ""}`, style: {
                        width: beforeSelectionColumn?.width ?? COLUMN_WIDTHS.EXPAND,
                        ...(stickyHeader && {
                            position: "sticky",
                            top: 46,
                            zIndex: 10,
                            backgroundColor: '#f4f4f5',
                        })
                    }, align: beforeSelectionColumn?.align ?? "center", children: beforeSelectionColumn?.header ?? null })), showSelectionColumn && (_jsx(TableCell, { className: "reusable-table-header-cell", style: {
                        width: COLUMN_WIDTHS.SELECTION,
                        ...(stickyHeader && {
                            position: "sticky",
                            top: 46,
                            zIndex: 10,
                            backgroundColor: '#f4f4f5',
                        })
                    }, align: "center", children: enableSelection && (_jsx(Checkbox, { className: "table-checkbox", icon: _jsx("span", { className: "table-checkbox-icon" }), checkedIcon: _jsx("span", { className: "table-checkbox-icon table-checkbox-icon-checked" }), indeterminateIcon: _jsx("span", { className: "table-checkbox-icon table-checkbox-icon-indeterminate" }), indeterminate: isIndeterminate, checked: isAllSelected, onChange: handleSelectAll, size: "small" })) })), normalizedColumns.all.map((column) => {
                    if (column.children && column.children.length > 0) {
                        return column.children.map((child) => (_jsx(TableCell, { "data-column-id": child.id, className: `reusable-table-header-cell ${child.sortable && onSortChange
                                ? "reusable-table-header-cell-sortable"
                                : ""}`, style: {
                                width: getColumnWidth(child),
                                textAlign: getColumnAlign(child),
                                borderLeft: dragOverColumnId === child.id ? "3px solid #ff008e" : undefined,
                                ...(stickyHeader && {
                                    position: "sticky",
                                    top: 46, // Height of the first row
                                    zIndex: 10,
                                    backgroundColor: '#f4f4f5',
                                })
                            }, align: getColumnAlign(child), onDragOver: (e) => handleHeaderDragOver(e, child.id), onDragLeave: handleHeaderDragLeave, onDrop: (e) => handleHeaderDrop(e, child.id), children: renderColumnHeader(child) }, child.id)));
                    }
                    return null;
                })] }, "header-child"));
        // Filter row for grouped columns
        if (enableColumnFilters && showFilterRow) {
            headerRows.push(_jsxs(TableRow, { className: "reusable-table-filter-row", children: [showExpandColumn && _jsx(TableCell, {}), showBeforeSelectionColumn && _jsx(TableCell, {}), showSelectionColumn && _jsx(TableCell, {}), normalizedColumns.all.map((column) => {
                        if (column.children && column.children.length > 0) {
                            return column.children.map((child) => (_jsx(TableCell, { "data-column-id": child.id, sx: { p: 1 }, children: renderFilterCell(child) }, child.id)));
                        }
                        return (_jsx(TableCell, { "data-column-id": column.id, sx: { p: 1 }, children: renderFilterCell(column) }, column.id));
                    })] }, "filter-row"));
        }
        return headerRows;
    }, [
        columns,
        showSelectionColumn,
        showExpandColumn,
        showBeforeSelectionColumn,
        enableSelection,
        isIndeterminate,
        isAllSelected,
        handleSelectAll,
        normalizedColumns,
        getColumnAlign,
        getColumnWidth,
        onSortChange,
        dragOverColumnId,
        handleHeaderDragOver,
        handleHeaderDragLeave,
        handleHeaderDrop,
        renderColumnHeader,
        enableColumnFilters,
        showFilterRow,
        renderFilterCell,
        beforeSelectionColumn,
    ]);
    const renderHeaderCell = useCallback((column, isPinned = false, pinPosition = null, isBoundary = false) => {
        const isSortable = column.sortable && onSortChange;
        const isSorted = sortConfig?.columnId === column.id;
        const sortDirection = isSorted ? sortConfig.direction : SORT_DIRECTIONS.ASC;
        const pinnedStyles = isPinned && pinPosition
            ? {
                position: "sticky",
                [pinPosition]: pinPosition === "left" ? leftPinnedOffsets[column.id] : rightPinnedOffsets[column.id],
                zIndex: Z_INDEX.PINNED_COLUMN,
                backgroundColor: column.headerSx?.backgroundColor || '#f4f4f5',
            }
            : {};
        const boundaryClass = isBoundary
            ? (pinPosition === "left" ? "reusable-table-header-cell-pinned-left-last" : "reusable-table-header-cell-pinned-right-first")
            : "";
        return (_jsx(TableCell, { "data-column-id": column.id, sx: column.headerSx, className: `reusable-table-header-cell ${isPinned
                ? `reusable-table-header-cell-pinned-${pinPosition}`
                : ""} ${isSortable ? "reusable-table-header-cell-sortable" : ""} ${draggedColumnId === column.id ? "dragging" : ""} ${dragOverColumnId === column.id ? "drag-over" : ""} ${boundaryClass}`, onDragOver: (e) => handleHeaderDragOver(e, column.id), onDragLeave: handleHeaderDragLeave, onDrop: (e) => handleHeaderDrop(e, column.id), style: {
                width: getColumnWidth(column),
                textAlign: getColumnAlign(column),
                opacity: draggedColumnId === column.id ? 0.5 : 1,
                borderLeft: dragOverColumnId === column.id ? "3px solid #ff008e" : undefined,
                ...(stickyHeader && {
                    backgroundColor: column.headerSx?.backgroundColor || '#f4f4f5', // ✅ Important for sticky — match .reusable-table-header-cell's own background
                }),
                ...pinnedStyles,
            }, align: getColumnAlign(column), children: column.renderHeader
                ? column.renderHeader(column, sortDirection)
                : renderColumnHeader(column) }, column.id));
    }, [
        onSortChange,
        sortConfig,
        leftPinnedOffsets,
        rightPinnedOffsets,
        draggedColumnId,
        dragOverColumnId,
        handleHeaderDragOver,
        handleHeaderDragLeave,
        handleHeaderDrop,
        getColumnWidth,
        getColumnAlign,
        stickyHeader,
        renderColumnHeader,
    ]);
    const renderDataCell = useCallback((row, column, isPinned = false, pinPosition = null, isBoundary = false) => {
        const pinnedStyles = isPinned && pinPosition
            ? {
                position: "sticky",
                [pinPosition]: pinPosition === "left" ? leftPinnedOffsets[column.id] : rightPinnedOffsets[column.id],
                zIndex: Z_INDEX.PINNED_CELL,
                backgroundColor: "white",
            }
            : {};
        const boundaryClass = isBoundary
            ? (pinPosition === "left" ? "reusable-table-cell-pinned-left-last" : "reusable-table-cell-pinned-right-first")
            : "";
        return (_jsx(TableCell, { "data-column-id": column.id, className: `reusable-table-cell ${isPinned ? `reusable-table-cell-pinned-${pinPosition}` : ""} ${boundaryClass} ${column.className || ""}`, style: {
                width: getColumnWidth(column),
                textAlign: getColumnAlign(column),
                ...pinnedStyles,
            }, align: getColumnAlign(column), sx: column.cellSx ? column.cellSx(row) : undefined, children: column.renderCell
                ? column.renderCell(row, column)
                : renderCell
                    ? renderCell(row, column)
                    : row[column.id] || "" }, column.id));
    }, [getColumnWidth, getColumnAlign, renderCell, leftPinnedOffsets, rightPinnedOffsets]);
    // Error State
    if (isError) {
        if (renderError) {
            return renderError();
        }
        return (_jsx(Box, { className: "reusable-table-container", children: _jsx(Box, { className: "reusable-table-error", sx: { p: 4, textAlign: "center" }, children: _jsx(Typography, { color: "error", children: DEFAULT_MESSAGES.ERROR_LOADING }) }) }));
    }
    // Full Loading State
    if (showFullLoading) {
        if (renderLoading) {
            return renderLoading();
        }
        return (_jsx(Box, { className: "reusable-table-container", sx: { flexGrow: 1, display: 'flex', flexDirection: 'column' }, children: _jsx(Box, { className: "reusable-table-loading", sx: {
                    flexGrow: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    p: 4,
                    minHeight: '400px'
                }, children: _jsx(CommonLoader, { className: 'table-loader' }) }) }));
    }
    const hasGroupedCols = hasGroupedColumns(columns);
    return (_jsxs(Box, { className: "reusable-table-container", children: [_jsx(TableContainer, { sx: {
                    ...(stickyHeader && {
                        maxHeight: maxHeight,
                        overflow: 'auto',
                    }),
                }, children: _jsxs(Table, { className: `reusable-table ${type || ""}`, stickyHeader: stickyHeader, children: [_jsx(TableHead, { children: hasGroupedCols ? (renderGroupedHeader()) : (_jsxs(_Fragment, { children: [_jsxs(TableRow, { className: "reusable-table-header", children: [showExpandColumn && (_jsx(TableCell, { className: "reusable-table-header-cell reusable-table-header-cell-expand", style: { width: COLUMN_WIDTHS.EXPAND }, children: onExpandAllToggle && (_jsx(IconButton, { size: "small", onClick: () => {
                                                        const isAnyExpanded = controlledExpandedRows ? controlledExpandedRows.size > 0 : false;
                                                        onExpandAllToggle(!isAnyExpanded);
                                                    }, sx: { p: 0 }, children: _jsx(KeyboardArrowDownIcon, { sx: {
                                                            fontSize: 18,
                                                            transform: (controlledExpandedRows && controlledExpandedRows.size > 0) ? "rotate(0deg)" : "rotate(-90deg)",
                                                            transition: "transform 0.2s"
                                                        } }) })) })), showBeforeSelectionColumn && (_jsx(TableCell, { className: `reusable-table-header-cell ${beforeSelectionColumn?.className || ""}`, style: { width: beforeSelectionColumn?.width ?? COLUMN_WIDTHS.EXPAND }, align: beforeSelectionColumn?.align ?? "center", children: beforeSelectionColumn?.header ?? null })), showSelectionColumn && (_jsx(TableCell, { className: `reusable-table-header-cell ${pinnedColumns.left.includes("selection") ? "reusable-table-header-cell-pinned-left" : ""}`, style: {
                                                    width: COLUMN_WIDTHS.SELECTION,
                                                    ...(pinnedColumns.left.includes("selection") && {
                                                        position: "sticky",
                                                        left: leftPinnedOffsets["selection"],
                                                        zIndex: Z_INDEX.PINNED_COLUMN,
                                                        backgroundColor: "#f4f4f5"
                                                    })
                                                }, align: "center", children: enableSelection && (_jsx(Checkbox, { className: "table-checkbox", icon: _jsx("span", { className: "table-checkbox-icon" }), checkedIcon: _jsx("span", { className: "table-checkbox-icon table-checkbox-icon-checked" }), indeterminateIcon: _jsx("span", { className: "table-checkbox-icon table-checkbox-icon-indeterminate" }), indeterminate: isIndeterminate, checked: isAllSelected, onChange: handleSelectAll, size: "small" })) })), normalizedColumns.leftPinned.map((column, index) => renderHeaderCell(column, true, "left", index === normalizedColumns.leftPinned.length - 1 // is last
                                            )), normalizedColumns.unpinned.map((column) => renderHeaderCell(column)), normalizedColumns.rightPinned.map((column, index) => renderHeaderCell(column, true, "right", index === 0 // is first
                                            ))] }), enableColumnFilters && showFilterRow && (_jsxs(TableRow, { className: "reusable-table-filter-row", children: [showExpandColumn && (_jsx(TableCell, { sx: {
                                                    p: 1,
                                                    ...(pinnedColumns.left.includes("expand") && {
                                                        position: "sticky",
                                                        left: leftPinnedOffsets["expand"],
                                                        zIndex: Z_INDEX.PINNED_COLUMN,
                                                        backgroundColor: "white"
                                                    })
                                                } })), showBeforeSelectionColumn && _jsx(TableCell, {}), showSelectionColumn && (_jsx(TableCell, { sx: {
                                                    p: 1,
                                                    ...(pinnedColumns.left.includes("selection") && {
                                                        position: "sticky",
                                                        left: leftPinnedOffsets["selection"],
                                                        zIndex: Z_INDEX.PINNED_COLUMN,
                                                        backgroundColor: "white"
                                                    })
                                                } })), normalizedColumns.leftPinned.map((column, index) => (_jsx(TableCell, { className: index === normalizedColumns.leftPinned.length - 1 ? 'reusable-table-cell-pinned-left-last' : '', sx: {
                                                    p: 1,
                                                    position: "sticky",
                                                    left: leftPinnedOffsets[column.id],
                                                    zIndex: Z_INDEX.PINNED_COLUMN,
                                                    backgroundColor: "white",
                                                }, children: renderFilterCell(column) }, column.id))), normalizedColumns.unpinned.map((column) => {
                                                if (column.children && column.children.length > 0) {
                                                    return column.children.map((child) => (_jsx(TableCell, { sx: { p: 1 }, children: renderFilterCell(child) }, child.id)));
                                                }
                                                return (_jsx(TableCell, { sx: { p: 1 }, children: renderFilterCell(column) }, column.id));
                                            }), normalizedColumns.rightPinned.map((column, index) => (_jsx(TableCell, { className: index === 0 ? 'reusable-table-cell-pinned-right-first' : '', sx: {
                                                    p: 1,
                                                    position: "sticky",
                                                    right: rightPinnedOffsets[column.id],
                                                    zIndex: Z_INDEX.PINNED_COLUMN,
                                                    backgroundColor: "white",
                                                }, children: renderFilterCell(column) }, column.id)))] }))] })) }), _jsxs(TableBody, { className: "reusable-table-body", children: [showBodyLoading && (_jsx(TableRow, { children: _jsx(TableCell, { colSpan: (showSelectionColumn ? 1 : 0) +
                                            (showBeforeSelectionColumn ? 1 : 0) +
                                            (showExpandColumn ? 1 : 0) +
                                            visibleColumns.length, align: "center", sx: { py: 4 }, children: _jsx(CommonLoader, { className: 'table-loader' }) }) })), !showBodyLoading && (!rows || rows.length === 0) && (_jsx(TableRow, { children: _jsx(TableCell, { colSpan: (showSelectionColumn ? 1 : 0) +
                                            (showBeforeSelectionColumn ? 1 : 0) +
                                            (showExpandColumn ? 1 : 0) +
                                            visibleColumns.length, align: "center", sx: { py: 4 }, children: renderEmpty ? (renderEmpty()) : (_jsx(Typography, { color: "text.secondary", children: emptyMessage })) }) })), !showBodyLoading &&
                                    rows &&
                                    rows.length > 0 &&
                                    enableSummaryRow &&
                                    summaryRowPosition === SUMMARY_ROW_POSITIONS.TOP &&
                                    renderSummaryRow && (_jsxs(TableRow, { className: "reusable-table-summary-row", sx: {
                                        "& .MuiTableCell-root": {
                                            borderBottom: "1px solid #EAECF0",
                                            fontWeight: 700
                                        },
                                    }, children: [showExpandColumn && _jsx(TableCell, { sx: { backgroundColor: "#F9FAFB" } }), showBeforeSelectionColumn && _jsx(TableCell, { sx: { backgroundColor: "#F9FAFB" } }), showSelectionColumn && _jsx(TableCell, { sx: { backgroundColor: "#F9FAFB" } }), normalizedColumns.all.map((column) => {
                                            if (column.children && column.children.length > 0) {
                                                return column.children.map((child) => (_jsx(TableCell, { align: getColumnAlign(child), sx: { backgroundColor: child.headerSx?.backgroundColor || "#F9FAFB" }, children: renderSummaryRow(rows, child) }, child.id)));
                                            }
                                            return (_jsx(TableCell, { align: getColumnAlign(column), sx: { backgroundColor: column.headerSx?.backgroundColor || "#F9FAFB" }, children: renderSummaryRow(rows, column) }, column.id));
                                        })] })), !showBodyLoading &&
                                    rows.map((row, rowIndex) => {
                                        const rowId = row.id ?? rowIndex;
                                        const isExpanded = expandedRowsSet.has(rowId);
                                        const isSelected = selectedRowsSet.has(rowId);
                                        const extraRowProps = rowProps ? rowProps(row) : undefined;
                                        return (_jsxs(React.Fragment, { children: [_jsxs(TableRow, { className: `reusable-table-row ${isExpanded ? "reusable-table-row-expanded" : ""} ${expandTrigger === EXPAND_TRIGGERS.ROW
                                                        ? "reusable-table-row-clickable"
                                                        : ""} ${isDense ? "dense-row" : "normal-row"} ${isSelected ? "reusable-table-row-selected" : ""}`, hover: true, onClick: () => handleRowClick(row, rowId), selected: isSelected, sx: {
                                                        cursor: "default",
                                                        ...(rowSx ? rowSx(row) : undefined),
                                                        // Keep selection state but remove default MUI highlight
                                                        "&.Mui-selected": { backgroundColor: "transparent" },
                                                        "&.Mui-selected:hover": { backgroundColor: "transparent" },
                                                    }, ...(extraRowProps || {}), children: [showExpandColumn && (_jsx(TableCell, { className: "reusable-table-cell reusable-table-cell-expand", style: { width: COLUMN_WIDTHS.EXPAND }, onClick: (e) => {
                                                                if (expandTrigger === EXPAND_TRIGGERS.ICON) {
                                                                    handleExpandClick(rowId, e);
                                                                }
                                                            }, children: showExpandColumn && (!isRowExpandable || isRowExpandable(row)) && (_jsx(IconButton, { className: `reusable-table-expand-button ${isExpanded
                                                                    ? "reusable-table-expand-button-expanded"
                                                                    : ""}`, onClick: (e) => handleExpandClick(rowId, e), size: "small", children: _jsx(KeyboardArrowDownIcon, { sx: {
                                                                        fontSize: 18,
                                                                        width: 18,
                                                                        height: 18,
                                                                    } }) })) })), showBeforeSelectionColumn && (_jsx(TableCell, { className: `reusable-table-cell ${beforeSelectionColumn?.className || ""}`, style: { width: beforeSelectionColumn?.width ?? COLUMN_WIDTHS.EXPAND }, align: beforeSelectionColumn?.align ?? "center", onClick: (e) => e.stopPropagation(), children: beforeSelectionColumn?.renderCell
                                                                ? beforeSelectionColumn.renderCell(row)
                                                                : null })), showSelectionColumn && (_jsx(TableCell, { className: `reusable-table-cell reusable-table-cell-selection ${pinnedColumns.left.includes("selection") ? "reusable-table-cell-pinned-left" : ""}`, style: {
                                                                width: COLUMN_WIDTHS.SELECTION,
                                                                ...(pinnedColumns.left.includes("selection") && {
                                                                    position: "sticky",
                                                                    left: leftPinnedOffsets["selection"],
                                                                    zIndex: Z_INDEX.PINNED_CELL,
                                                                    backgroundColor: "white"
                                                                })
                                                            }, align: "center", children: enableSelection && (_jsx(Checkbox, { className: "table-checkbox", icon: _jsx("span", { className: "table-checkbox-icon" }), checkedIcon: _jsx("span", { className: "table-checkbox-icon table-checkbox-icon-checked" }), indeterminateIcon: _jsx("span", { className: "table-checkbox-icon table-checkbox-icon-indeterminate" }), checked: isSelected, disabled: isRowSelectable ? !isRowSelectable(row) : false, onChange: (e) => {
                                                                    if (!isRowSelectable || isRowSelectable(row)) {
                                                                        handleRowSelect(rowId, e);
                                                                    }
                                                                }, size: "small" })) })), normalizedColumns.leftPinned.map((column, index) => column.children && column.children.length > 0
                                                            ? column.children.map((child) => renderDataCell(row, child, true, "left", index === normalizedColumns.leftPinned.length - 1))
                                                            : renderDataCell(row, column, true, "left", index === normalizedColumns.leftPinned.length - 1)), normalizedColumns.unpinned.map((column) => column.children && column.children.length > 0
                                                            ? column.children.map((child) => renderDataCell(row, child))
                                                            : renderDataCell(row, column)), normalizedColumns.rightPinned.map((column, index) => column.children && column.children.length > 0
                                                            ? column.children.map((child) => renderDataCell(row, child, true, "right", index === 0))
                                                            : renderDataCell(row, column, true, "right", index === 0))] }), showExpandColumn && isExpanded && (_jsx(TableRow, { className: "reusable-table-row-expanded-content", children: _jsx(TableCell, { colSpan: (showSelectionColumn ? 1 : 0) +
                                                            (showBeforeSelectionColumn ? 1 : 0) +
                                                            (showExpandColumn ? 1 : 0) +
                                                            visibleColumns.length, className: expandCellClassName, children: renderExpandedContent && renderExpandedContent(row) }) }))] }, rowId));
                                    }), !showBodyLoading &&
                                    enableSummaryRow &&
                                    summaryRowPosition === SUMMARY_ROW_POSITIONS.BOTTOM &&
                                    renderSummaryRow && (_jsxs(TableRow, { className: "reusable-table-summary-row", sx: {
                                        position: "sticky",
                                        bottom: 0,
                                        zIndex: 6,
                                        boxShadow: "0 -1px 3px rgba(0,0,0,0.08)",
                                        "& .MuiTableCell-root": {
                                            borderTop: "1px solid #EAECF0",
                                            fontWeight: 700,
                                            backgroundColor: "#F9FAFB",
                                        },
                                    }, children: [showExpandColumn && _jsx(TableCell, { sx: { backgroundColor: "#F9FAFB" } }), showBeforeSelectionColumn && _jsx(TableCell, { sx: { backgroundColor: "#F9FAFB" } }), showSelectionColumn && _jsx(TableCell, { sx: { backgroundColor: "#F9FAFB" } }), [
                                            ...normalizedColumns.leftPinned.map((col, index) => ({ col, isPinned: true, pinPosition: 'left', isBoundary: index === normalizedColumns.leftPinned.length - 1 })),
                                            ...normalizedColumns.unpinned.map(col => ({ col, isPinned: false, pinPosition: null, isBoundary: false })),
                                            ...normalizedColumns.rightPinned.map((col, index) => ({ col, isPinned: true, pinPosition: 'right', isBoundary: index === 0 })),
                                        ].map(({ col: column, isPinned, pinPosition }) => {
                                            const pinnedStyles = isPinned && pinPosition
                                                ? {
                                                    position: "sticky",
                                                    [pinPosition]: pinPosition === "left" ? leftPinnedOffsets[column.id] : rightPinnedOffsets[column.id],
                                                    zIndex: 8,
                                                    backgroundColor: column.headerSx?.backgroundColor || "#F9FAFB",
                                                }
                                                : {};
                                            if (column.children && column.children.length > 0) {
                                                return column.children.map((child) => (_jsx(TableCell, { align: getColumnAlign(child), sx: { backgroundColor: child.headerSx?.backgroundColor || "#F9FAFB" }, children: renderSummaryRow(rows, child) }, child.id)));
                                            }
                                            return (_jsx(TableCell, { align: getColumnAlign(column), style: pinnedStyles, sx: { backgroundColor: column.headerSx?.backgroundColor || "#F9FAFB" }, children: renderSummaryRow(rows, column) }, column.id));
                                        })] }))] })] }) }), columnVisibilityConfig?.enable === true && (_jsx(ColumnVisibilityPanel, { open: Boolean(columnVisibilityAnchorEl), anchorEl: columnVisibilityAnchorEl, onClose: handleCloseColumnVisibility, columns: columns, allowedColumnIds: columnVisibilityConfig.columns || null, hiddenColumns: hiddenColumnsSet, onVisibilityChange: handleColumnVisibilityChange, columnOrder: columnOrder, onOrderChange: handleColumnOrderChange, enableColumnReorder: columnVisibilityConfig.enableReorder === true }))] }));
}
export default CommonTable;
